/**
 * Data models for the Digital Menu system.
 *
 * These are plain TypeScript interfaces — no decorators or class constructors.
 * The canonical data shape is defined by what's stored in Firestore.
 * Functions also have a minimal copy at functions/src/types.ts.
 */

// --- Identity models ---

export interface User {
  uid: string;
  email: string;
  displayName: string;
  roles: string[];
  orgs: string[];
  createdAt: Date;
}

export interface OrgFeatures {
  compositionImages?: boolean;
  vesselStrip?: boolean;
  canvasMenus?: boolean;
}

export interface Org {
  id: string;
  name: string;
  displayName: string;
  defaultOverlayTextFill?: string;
  defaultUserEmailDomain?: string;
  imageTags?: string[];
  overlayTextFont?: string;
  overlayTextFontWeight?: number;
  features?: OrgFeatures;
}

// --- Menu models ---

export interface ImageReference {
  /** Bucket-relative object path. The only thing new writes persist. */
  storageRef?: string;
  /**
   * Legacy absolute download URL. Read-only: documents written before image
   * URLs were resolved at read time still carry one. The cutover backfill
   * (scripts/backfill-storage-refs.mjs) adds a storageRef next to it only for
   * URLs into the legacy bucket. Nothing writes this field any more - an
   * absolute URL embeds the bucket name and breaks when the app moves to
   * another Firebase project.
   */
  downloadUrl?: string;
}

export interface ImageOverlay {
  // null means the user explicitly disabled the overlay (Firestore stores undefined as null).
  // undefined means the overlay was never configured (new item or legacy data).
  imageRef?: ImageReference | null;
  text?: string;
  textFill?: string;
  categoryId?: string;
}

export interface ImageBackground {
  color: string;
}

export interface ItemImage {
  imageRef: ImageReference;
  tag: string;
  overlay?: ImageOverlay;
  background?: ImageBackground;
  compositionId?: string;
}

export interface Option {
  label: string;
  price: number;
}

export interface OptionGroup {
  name: string;
  options: Option[];
  image?: ItemImage;
}

export enum CategoryType {
  Beverage = 'beverage',
  Food = 'food',
  Merchandise = 'merchandise',
  Promotional = 'promotional',
}

export interface Category {
  id: string;
  name: string;
  order: number;
  type: CategoryType;
  optionGroups: OptionGroup[];
  image?: ItemImage;
}

export enum MenuItemType {
  Beverage = 'beverage',
  SpecialItem = 'special-item',
}

export interface MenuItemInfo {
  id: string;
  name: string;
  category: Omit<Category, 'optionGroups' | 'image'>;
  description: string;
  menuDescription: string;
  menuItemType: string;
}

export enum BeverageStatus {
  Active = 'active',
  InProduction = 'in-production',
}

export interface Beverage {
  id: string;
  name: string;
  style: string;
  categoryId: string;
  category: Category;
  status: BeverageStatus;
  description: string;
  images: ItemImage[];
  menuItemType: MenuItemType;
  abv?: number;
}

export function getBeverageMenuDescription(b: Beverage): string {
  const fields = [b.style];
  if (b.abv) {
    fields.push(`${b.abv}%`);
  }
  fields.push(b.description);
  return fields.join(' - ');
}

export interface SpecialItem {
  id: string;
  name: string;
  categoryId: string;
  category: Category;
  description: string;
  images: ItemImage[];
  menuItemType: MenuItemType;
}

export function getSpecialItemMenuDescription(s: SpecialItem): string {
  return s.description;
}

export interface SlotConfig {
  row: number;
  col: number;
}

export function isSameSlot(a: SlotConfig | undefined, b: SlotConfig | undefined): boolean {
  return a !== undefined && b !== undefined && a.col === b.col && a.row === b.row;
}

export interface MenuItem {
  itemInfo: MenuItemInfo;
  options: Option[];
  /**
   * The name of the Option Group on the Item's Category that `options` was
   * copied from, so the server sync knows which prices to refresh. Absent on
   * entries made before it was recorded, and for Items without Option Groups.
   */
  optionGroupName?: string;
  images: Record<string, ItemImage>;
  slotConfig: SlotConfig;
  sortOrder: number;
  image?: ItemImage;
}

export enum MenuViewType {
  MenuBoard = 'menu-board',
  ItemsList = 'items-list',
}

export interface MenuBoardDisplayConfig {
  rows: number;
  columns: number;
}

/** The rows and columns of a new Classic Menu's grid, and of one whose size is unset. */
export const DEFAULT_GRID_SIZE = 4;

export interface Menu {
  id: string;
  name: string;
  orgId: string;
  menuBoardDisplayConfig: MenuBoardDisplayConfig;
  items: MenuItem[];
}

// --- Composition models ---

export interface CropConfig {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type CompositionStatus = 'draft' | 'rendered' | 'stale';
export type SourceItemType = 'beverages' | 'special-items';

export interface Composition {
  id: string;
  sourceItemId: string;
  sourceItemType: SourceItemType;
  productImageRef: ImageReference;
  cropConfig?: CropConfig;
  optionGroupIndex?: number;
  textFill?: string;
  nameFontFamily?: string;
  textFontFamily?: string;
  showStyleAbv?: boolean;
  renderedImageRef?: ImageReference;
  renderedAt?: Date;
  status: CompositionStatus;
  createdAt: Date;
  updatedAt: Date;
}

export function updateMenuSlotConfigsBySortOrder(menu: Pick<Menu, 'menuBoardDisplayConfig' | 'items'>): void {
  menu.items.sort((a, b) => a.sortOrder - b.sortOrder);

  let itemIndex = 0;
  const rows = menu.menuBoardDisplayConfig.rows || Math.max(...menu.items.map((v) => v.slotConfig.row)) + 1;
  const cols = menu.menuBoardDisplayConfig.columns || Math.max(...menu.items.map((v) => v.slotConfig.col)) + 1;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (itemIndex >= menu.items.length) return;
      menu.items[itemIndex].slotConfig = { row, col };
      itemIndex++;
    }
  }
}

// --- Canvas menu models (grid-based block builder) ---
// Design: docs/superpowers/specs/2026-06-10-canvas-menu-builder-design.md

export type CanvasOrientation = 'landscape' | 'portrait';

export const CANVAS_GRID: Record<CanvasOrientation, { cols: number; rows: number }> = {
  landscape: { cols: 48, rows: 27 },
  portrait: { cols: 27, rows: 48 },
};

export const CANVAS_MAX_BLOCKS = 100;
export const CANVAS_MAX_ROTATION_IMAGES = 20;
export const CANVAS_MIN_ROTATION_INTERVAL_SEC = 3;
export const CANVAS_MAX_PAGES = 10;
export const CANVAS_DEFAULT_PAGE_ROTATION_SEC = 10;
export const CANVAS_MIN_PAGE_ROTATION_SEC = 3;

export interface BlockFrame {
  x: number; // left edge in grid units
  y: number; // top edge in grid units
  w: number; // >= 1, width in grid units
  h: number; // >= 1, height in grid units
  z: number; // stacking order; editor keeps blocks non-overlapping for now
}

export type BlockAnimation = 'none' | 'fade' | 'slide-up' | 'zoom';

/**
 * Optional visibility window for a block on the public board. Empty `days`
 * means every day; omitting times means all day. Evaluated in the viewer's
 * local time — ideal for happy-hour or daypart blocks.
 */
export interface BlockSchedule {
  days?: number[];        // 0–6, Sun–Sat; empty/undefined = all days
  startTime?: string;     // 'HH:MM' 24h local
  endTime?: string;       // 'HH:MM' 24h local
}

export interface BlockBase {
  id: string;
  frame: BlockFrame;
  backgroundColor?: string;
  cornerRadius?: number;
  animation?: BlockAnimation;  // entrance animation on the public board
  schedule?: BlockSchedule;    // when the block is visible (public board only)
  page?: number;               // which board page this block belongs to; undefined = page 0
}

export interface RichTextBlock extends BlockBase {
  type: 'rich-text';
  content: string;
  fontFamily?: string;
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

export interface ImageBlock extends BlockBase {
  type: 'image';
  imageRef?: ImageReference;
  objectFit: 'cover' | 'contain';
  opacity?: number;
}

export interface RotatingImageBlock extends BlockBase {
  type: 'rotating-image';
  images: ImageReference[];
  intervalSec: number;
  transition: 'fade' | 'slide';
  shuffle?: boolean;
  objectFit: 'cover' | 'contain';
}

/** Curated typographic presets for menu-data blocks (SBD design system). */
export type CanvasTextPreset = 'classic' | 'chalkboard' | 'modern';

/** A single beverage or special-item rendered as an image + name/meta/price tile. */
export interface MenuItemBlock extends BlockBase {
  type: 'menu-item';
  itemId: string;
  itemType: SourceItemType; // 'beverages' | 'special-items'
  preset: CanvasTextPreset;
  accentColor?: string;
  fontFamily?: string;
  showImage?: boolean;       // default true
  showDescription?: boolean; // default true
  showPrice?: boolean;       // default true
}

/** A live list of every item in a category with names + prices. */
export interface CategoryListBlock extends BlockBase {
  type: 'category-list';
  categoryId: string;
  preset: CanvasTextPreset;
  accentColor?: string;
  fontFamily?: string;
  columns?: number;          // 1 or 2; default 1
  showPrice?: boolean;       // default true
}

export type CanvasBlock =
  | RichTextBlock
  | ImageBlock
  | RotatingImageBlock
  | MenuItemBlock
  | CategoryListBlock;

/** Which page a block belongs to; blocks without a `page` live on page 0. */
export function blockPage(block: CanvasBlock): number {
  return block.page ?? 0;
}

export interface CanvasMenu {
  id: string;
  kind: 'canvas';
  name: string;
  orgId: string;
  orientation: CanvasOrientation; // set at creation, immutable
  backgroundColor: string;            // draft background (autosaved)
  backgroundImageRef?: ImageReference | null; // draft background image
  publishedBackgroundColor?: string;  // written only on publish
  publishedBackgroundImageRef?: ImageReference | null;
  draftBlocks: CanvasBlock[];
  publishedBlocks?: CanvasBlock[];
  publishedAt?: Date;
  updatedAt: Date;
  // Multi-page boards (additive; absent/1 behaves as a single-page canvas).
  pageCount?: number;              // draft page count, default 1
  pageRotationSec?: number;        // draft cycle interval, default 10
  publishedPageCount?: number;     // written only on publish
  publishedPageRotationSec?: number;
}

// orgs/{orgId}/menus/{menuId}/publish-history/{snapshotId}
export interface PublishSnapshot {
  id: string;
  blocks: CanvasBlock[];
  backgroundColor: string;
  backgroundImageRef?: ImageReference | null;
  publishedAt: Date;
  publishedBy: string;
  pageCount?: number;
  pageRotationSec?: number;
}
